/**
 * CuttySark 賞セッションのルール（権限・進行の遷移・票の検証・集計・閲覧者ごとの見え方）。
 *
 * 判定はすべてここに寄せ、API はこれを呼ぶだけにする。画面と MCP は API が返した結果と
 * エラーメッセージをそのまま見せる（入口ごとに判定を書くと「MCP からは投票できるが画面からは
 * できない」がサイレントに起きる）。I/O を持たない純粋関数なので、そのまま単体テストできる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの権限・進行遷移・投票検証・集計・閲覧範囲の判定 (純粋関数の SSOT)
 * @graph-connects none
 */
import { CUTTYSARK_ANNOUNCED_RANKS } from './cuttysark.js';
/** セッションを作れるロール。各チームの予選はリーダーが回すため team_leader 以上。@graph-connects none */
const CREATOR_ROLES = new Set(['admin', 'group_manager', 'team_leader']);
/**
 * セッションを作れるか（PDG としてページを使える、チームリーダー以上の人）。
 * @graph-connects none
 */
export function canCreateCuttysarkSession(viewer) {
    return viewer.hasPageAccess && viewer.portalRoles.some((role) => CREATOR_ROLES.has(role));
}
/**
 * その回に候補者・投票者・運営として登録されているか。
 * @graph-connects none
 */
export function isCuttysarkSessionMember(session, email) {
    return (session.operators.includes(email) ||
        session.voters.includes(email) ||
        session.candidates.some((c) => c.email === email));
}
/**
 * その回を開けるか。PDG（と admin）は全回、それ以外（兼務者など）は登録されている回だけ。
 * 所属グループが 1 つしか持てないため、兼務者は PDG と判定されない。運営が登録すれば
 * コードの変更なしに開けるようにする。
 * @graph-connects isCuttysarkSessionMember [calls] 登録されているかの判定
 */
export function canViewCuttysarkSession(session, viewer) {
    return (viewer.hasPageAccess ||
        viewer.portalRoles.includes('admin') ||
        isCuttysarkSessionMember(session, viewer.email));
}
/**
 * 運営として操作できるか（admin は全セッションを操作できる）。
 * @graph-connects none
 */
export function canOperateCuttysarkSession(session, viewer) {
    return viewer.portalRoles.includes('admin') || session.operators.includes(viewer.email);
}
/**
 * 投票できる人か（受付中かどうかは見ない）。
 * @graph-connects none
 */
export function isCuttysarkVoter(session, email) {
    return session.voterMode === 'anyone' || session.voters.includes(email);
}
/**
 * 候補者と部門を変えてよい進行状態。ただし 1 票でも入っていれば変えない（判定は設定更新側）。
 * @graph-connects none
 */
export const PRE_VOTING_PHASES = new Set([
    'preparing',
    'intro',
    'presenting',
]);
/**
 * 投票を受け付ける進行状態。代表者紹介から発表・採点までは 1 人ずつ進むので、紹介が始まったら
 * その場で採点・投票できるようにする（全員の発表が終わるまで待たせない）。
 * @graph-connects none
 */
export const VOTING_OPEN_PHASES = new Set([
    'intro',
    'presenting',
    'voting',
]);
/**
 * 部門の結果発表で、発表を 1 つ進めるごとに下位から何人めくった状態になるか（発表順）。
 * 3 位以内の順位を低い順に 1 つずつ（同順位はまとめて）発表し、4 位以下は最初の発表で一緒に開く。
 * entries は集計結果の並び（順位の高い順）を前提にする。
 * @graph-connects none
 */
export function cuttysarkRevealCounts(entries) {
    const ranks = [...new Set(entries.map((e) => e.rank))]
        .filter((rank) => rank <= CUTTYSARK_ANNOUNCED_RANKS)
        .sort((a, b) => b - a);
    return ranks.map((rank) => entries.filter((e) => e.rank >= rank).length);
}
/**
 * 下位から count 人めくった状態で、いちばん上に出ている順位（まだ何もめくっていなければ null）。
 * 運営画面の「次は第 N 位」の表示とステージ画面の発表中の順位に使う。
 * @graph-connects none
 */
export function cuttysarkRevealedTopRank(entries, count) {
    if (count <= 0 || entries.length === 0)
        return null;
    return entries[Math.max(entries.length - count, 0)]?.rank ?? null;
}
/**
 * 順位単位で 1 つ進めた・戻した後のめくり人数。進める先・戻す先が無ければ null。
 * @graph-connects cuttysarkRevealCounts [calls] 発表ごとのめくり人数
 */
function steppedRevealCount(entries, current, direction) {
    const counts = cuttysarkRevealCounts(entries);
    if (direction === 'next')
        return counts.find((c) => c > current) ?? null;
    return current > 0 ? ([0, ...counts].filter((c) => c < current).at(-1) ?? 0) : null;
}
/**
 * 次に発表する順位（もう残っていなければ null）。運営画面のボタンの表示に使う。
 * @graph-connects steppedRevealCount [calls] 1 つ進めた後のめくり人数
 * @graph-connects cuttysarkRevealedTopRank [calls] めくった状態のいちばん上の順位
 */
export function nextCuttysarkRevealRank(entries, current) {
    const next = steppedRevealCount(entries, current, 'next');
    return next === null ? null : cuttysarkRevealedTopRank(entries, next);
}
/**
 * 進行状態の遷移として許すか。結果発表と終了は締切後からしか入れない（集計していない結果を
 * 出さないため）。それ以外の前後移動（投票受付中から紹介・発表へ戻す、締切後に投票を再開する等）は
 * 運営の判断に任せて許す。票が入った後に候補者を差し替えられないようにする守りは、遷移ではなく
 * 設定更新側（票があれば部門・候補者を変えない）で持つ。
 * @graph-connects none
 */
export function canTransitionCuttysarkPhase(from, to) {
    if (to === 'revealing')
        return from === 'closed' || from === 'revealing' || from === 'finished';
    if (to === 'finished')
        return from === 'closed' || from === 'revealing' || from === 'finished';
    return true;
}
/**
 * 進行操作を適用した後のセッションを返す。不正なら日本語のエラーメッセージを返す。
 * 締切（closed への遷移）で集計を焼き込んで開示状態を消し、投票の再開で集計と開示状態を消す。
 * @graph-connects tallyCuttysarkResults [calls] 締切時の集計
 */
export function applyCuttysarkControl(session, control, ballots) {
    let next = { ...session };
    if (control.phase !== undefined && control.phase !== session.phase) {
        if (!canTransitionCuttysarkPhase(session.phase, control.phase)) {
            return { ok: false, error: '結果発表と終了は、投票を締め切ってからにしてください' };
        }
        next = { ...next, phase: control.phase };
        if (control.phase === 'closed') {
            next = { ...next, results: tallyCuttysarkResults(session, ballots), reveal: [] };
        }
        if (control.phase === 'voting') {
            next = { ...next, results: null, reveal: [] };
        }
        // 締切・結果発表の後に受付中の段階へ戻したら、集計と開示を消す（再締切で集計し直す）
        if (PRE_VOTING_PHASES.has(control.phase) || VOTING_OPEN_PHASES.has(control.phase)) {
            next = { ...next, results: null, reveal: [] };
        }
    }
    if (control.currentCandidateEmail !== undefined) {
        const email = control.currentCandidateEmail;
        if (email !== null && !session.candidates.some((c) => c.email === email)) {
            return { ok: false, error: `${email} は候補者に登録されていません` };
        }
        next = { ...next, currentCandidateEmail: email };
    }
    if (control.reveal !== undefined) {
        if (next.phase !== 'revealing') {
            return { ok: false, error: '結果は結果発表中にだけめくれます' };
        }
        const { divisionId, count } = control.reveal;
        const total = session.candidates.filter((c) => c.divisionId === divisionId).length;
        if (!session.divisions.some((d) => d.id === divisionId)) {
            return { ok: false, error: `部門 ${divisionId} はありません` };
        }
        if (count > total) {
            return { ok: false, error: `この部門の候補者は ${String(total)} 人です` };
        }
        const others = next.reveal.filter((r) => r.divisionId !== divisionId);
        next = { ...next, reveal: [...others, { divisionId, count }] };
    }
    if (control.revealStep !== undefined) {
        const { divisionId, direction } = control.revealStep;
        if (next.phase !== 'revealing' || next.results === null) {
            return { ok: false, error: '結果は結果発表中にだけめくれます' };
        }
        const result = next.results.find((r) => r.divisionId === divisionId);
        if (!result)
            return { ok: false, error: `部門 ${divisionId} はありません` };
        const current = next.reveal.find((r) => r.divisionId === divisionId)?.count ?? 0;
        const count = steppedRevealCount(result.entries, current, direction);
        if (count === null) {
            return {
                ok: false,
                error: direction === 'next'
                    ? 'この部門で発表する順位はもう残っていません'
                    : 'この部門はまだ何も発表していません',
            };
        }
        // 最後に動かした部門を末尾に置く（ステージ画面はこの部門の発表を出す）
        const others = next.reveal.filter((r) => r.divisionId !== divisionId);
        next = { ...next, reveal: [...others, { divisionId, count }] };
    }
    return { ok: true, session: next };
}
/**
 * 投票を検証する。受付中であること・投票できる人であること・部門ごとの票数・自分や
 * 他部門の人への投票・重複・採点の対象を確かめる。問題があれば日本語のメッセージを並べて返す。
 * 票数は上限だけを見て、使い切らなくても受け付ける（棄権や途中保存を許すため）。
 * @graph-connects none
 */
export function validateCuttysarkBallot(session, voterEmail, input) {
    const errors = [];
    if (!VOTING_OPEN_PHASES.has(session.phase))
        errors.push('いまは投票を受け付けていません');
    if (!isCuttysarkVoter(session, voterEmail))
        errors.push('このセッションの投票者ではありません');
    const candidateDivision = new Map(session.candidates.map((c) => [c.email, c.divisionId]));
    const seenDivisions = new Set();
    for (const vote of input.votes) {
        const division = session.divisions.find((d) => d.id === vote.divisionId);
        if (!division) {
            errors.push(`部門 ${vote.divisionId} はありません`);
            continue;
        }
        if (seenDivisions.has(vote.divisionId))
            errors.push(`${division.name} の票が重複しています`);
        seenDivisions.add(vote.divisionId);
        if (vote.candidateEmails.length > division.votesPerVoter) {
            errors.push(`${division.name} は 1 人 ${String(division.votesPerVoter)} 票までです`);
        }
        if (new Set(vote.candidateEmails).size !== vote.candidateEmails.length) {
            errors.push(`${division.name} で同じ人に複数票は入れられません`);
        }
        for (const email of vote.candidateEmails) {
            if (email === voterEmail)
                errors.push('自分には投票できません');
            else if (candidateDivision.get(email) !== vote.divisionId) {
                errors.push(`${email} は ${division.name} の候補者ではありません`);
            }
        }
    }
    const scoredEmails = new Set();
    for (const score of input.scores) {
        if (scoredEmails.has(score.candidateEmail)) {
            errors.push('同じ候補者を複数回採点しています');
        }
        scoredEmails.add(score.candidateEmail);
        if (score.candidateEmail === voterEmail)
            errors.push('自分は採点できません');
        else if (!candidateDivision.has(score.candidateEmail)) {
            errors.push(`${score.candidateEmail} は候補者ではありません`);
        }
    }
    return errors;
}
/**
 * 部門ごとに得票を数えて順位を付ける。同票は同順位、並びは得票の多い順・同票は登録順。
 * 次の段階へ進む枠の境目で同票なら tiedAtBoundary を立て、運営が決められるようにする。
 * コメントは投票者を伏せ、投票した順に並べる。
 * @graph-connects none
 */
export function tallyCuttysarkResults(session, ballots) {
    const orderedBallots = [...ballots].sort((a, b) => a.submittedAt.localeCompare(b.submittedAt));
    return session.divisions.map((division) => {
        const candidates = session.candidates.filter((c) => c.divisionId === division.id);
        const counted = candidates.map((candidate, order) => {
            const votes = orderedBallots.filter((b) => b.votes.some((v) => v.divisionId === division.id && v.candidateEmails.includes(candidate.email))).length;
            const comments = orderedBallots.flatMap((b) => b.scores
                .filter((s) => s.candidateEmail === candidate.email && s.comment !== '')
                .map((s) => s.comment));
            return { candidateEmail: candidate.email, votes, order, comments };
        });
        counted.sort((a, b) => b.votes - a.votes || a.order - b.order);
        const ranked = counted.map((entry) => ({
            ...entry,
            rank: counted.filter((other) => other.votes > entry.votes).length + 1,
        }));
        // 枠の最後の人と同票の人まで含めると枠を超える場合だけ、境目の同票として運営に判断を委ねる
        const boundaryVotes = ranked[division.advanceCount - 1]?.votes;
        const overflowsAtBoundary = boundaryVotes !== undefined &&
            ranked.filter((r) => r.votes >= boundaryVotes).length > division.advanceCount;
        const entries = ranked.map(({ candidateEmail, votes, rank, comments }) => ({
            candidateEmail,
            votes,
            rank,
            advancing: rank <= division.advanceCount,
            tiedAtBoundary: overflowsAtBoundary && votes === boundaryVotes,
            comments,
        }));
        return { divisionId: division.id, entries };
    });
}
/**
 * 閲覧者に見せてよい範囲の結果。運営には締切後の全結果、それ以外には結果発表中に
 * めくった分（下位から count 人）だけ、終了後は全結果を返す。
 * @graph-connects none
 */
function visibleResults(session, isOperator) {
    if (session.results === null)
        return [];
    if (isOperator || session.phase === 'finished')
        return session.results;
    if (session.phase !== 'revealing')
        return [];
    return session.results.map((result) => {
        const count = session.reveal.find((r) => r.divisionId === result.divisionId)?.count ?? 0;
        const start = Math.max(result.entries.length - count, 0);
        return { divisionId: result.divisionId, entries: result.entries.slice(start) };
    });
}
/**
 * アップロードした写真の配信パス（API のベース URL からの相対）。版を付けてキャッシュを切り替える。
 * @graph-connects none
 */
export function cuttysarkCandidatePhotoPath(sessionId, email, version) {
    return `/cuttysark/sessions/${encodeURIComponent(sessionId)}/candidates/${encodeURIComponent(email)}/photo?v=${String(version)}`;
}
/**
 * 閲覧者ごとに絞ったセッションの見え方を組み立てる。
 *
 * 投票済みの人はセッション本体に持たず、投票ドキュメントの有無から呼び出し側が渡す。
 * 投票のたびにセッション本体へ書くと、全員が一斉に投票したときに同じドキュメントへの
 * 書き込みが集中して詰まるため。運営なら全投票者、それ以外なら閲覧者本人の分だけ渡せばよい。
 * @graph-connects canOperateCuttysarkSession [calls] 運営かどうかの判定
 * @graph-connects isCuttysarkVoter [calls] 投票者かどうかの判定
 */
export function buildCuttysarkLiveView(id, session, viewer, submittedVoters) {
    const isOperator = canOperateCuttysarkSession(session, viewer);
    const submitted = new Set(submittedVoters);
    return {
        id,
        title: session.title,
        meetUrl: session.meetUrl,
        quarter: session.quarter,
        stage: session.stage,
        divisions: session.divisions,
        candidates: session.candidates.map((c) => ({
            ...c,
            uploadedPhotoPath: c.photoVersion > 0 ? cuttysarkCandidatePhotoPath(id, c.email, c.photoVersion) : '',
        })),
        phase: session.phase,
        currentCandidateEmail: session.currentCandidateEmail,
        reveal: session.reveal,
        results: visibleResults(session, isOperator),
        revision: session.revision,
        viewer: {
            email: viewer.email,
            isOperator,
            canVote: isCuttysarkVoter(session, viewer.email),
            hasSubmitted: submitted.has(viewer.email),
        },
        operatorView: isOperator
            ? {
                voterMode: session.voterMode,
                voters: session.voters,
                operators: session.operators,
                submittedVoters: [...submittedVoters],
                pendingVoters: session.voterMode === 'listed' ? session.voters.filter((v) => !submitted.has(v)) : [],
            }
            : null,
    };
}
/**
 * 設定更新後の部門・候補者の整合を確かめる（候補者の重複・存在しない部門・部門 ID の重複）。
 * @graph-connects none
 */
export function validateCuttysarkSetup(session) {
    const errors = [];
    const divisionIds = session.divisions.map((d) => d.id);
    if (new Set(divisionIds).size !== divisionIds.length)
        errors.push('部門 ID が重複しています');
    const emails = session.candidates.map((c) => c.email);
    const duplicated = emails.filter((email, i) => emails.indexOf(email) !== i);
    for (const email of new Set(duplicated))
        errors.push(`${email} が候補者に重複しています`);
    for (const candidate of session.candidates) {
        if (!divisionIds.includes(candidate.divisionId)) {
            errors.push(`${candidate.email} の部門 ${candidate.divisionId} がありません`);
        }
    }
    return errors;
}
/**
 * 設定更新の入力が、部門・候補者を今から変えるものか。値を送っていても今と同じなら変更なしとみなす。
 * 運営画面は投票者だけを変えるときも部門・候補者を一緒に送るため、送ったかどうかで判定すると
 * 投票開始後に投票者を足せなくなる。
 * @graph-connects none
 */
export function cuttysarkLineupChanged(current, input) {
    const divisionKey = (d) => JSON.stringify([d.id, d.name, d.votesPerVoter, d.advanceCount]);
    const candidateKey = (c) => JSON.stringify([
        c.email,
        c.divisionId,
        c.title ?? '',
        c.intro ?? '',
        c.materialUrl ?? '',
        c.photoUrl ?? '',
    ]);
    const sameList = (a, b, key) => a.length === b.length && a.every((v, i) => key(v) === key(b[i]));
    if (input.divisions !== undefined && !sameList(input.divisions, current.divisions, divisionKey)) {
        return true;
    }
    return (input.candidates !== undefined && !sameList(input.candidates, current.candidates, candidateKey));
}
/**
 * 終了後に、本人あての応援コメントを返す（投票者は伏せる）。終了前と、候補者でない人は null。
 * 締切時に焼き込んだ集計のコメントを使うので、投票ドキュメントは読まない。
 * @graph-connects none
 */
export function myCuttysarkComments(session, email) {
    if (session.phase !== 'finished' || session.results === null)
        return null;
    const candidate = session.candidates.find((c) => c.email === email);
    if (!candidate)
        return null;
    const entry = session.results
        .find((r) => r.divisionId === candidate.divisionId)
        ?.entries.find((e) => e.candidateEmail === email);
    const divisionName = session.divisions.find((d) => d.id === candidate.divisionId)?.name ?? '';
    return { divisionName, comments: entry?.comments ?? [] };
}
//# sourceMappingURL=cuttysark-rules.js.map